import { Currency } from "@redotech/money/currencies";
import { z } from "zod";
import { zExt } from "../../common/zod-util";
import {
  Carriers,
  FedexServices,
  ServiceLevel,
  UpsServices,
  UspsServices,
} from "../../fulfillments/fulfillment-carriers-and-services";

export enum RateTableType {
  FIXED = "fixed",
  DYNAMIC = "dynamic",
}

export enum ConditionalPricingCondition {
  WEIGHT = "weight",
  PRICE = "price",
}

export enum DynamicRateSelection {
  CHEAPEST = "cheapest",
  FASTEST = "fastest",
}

export const ShippingRateLocationSchema = z.object({
  country: z.string(),
  provinces: z.array(z.string()),
});
export type ShippingRateLocation = z.infer<typeof ShippingRateLocationSchema>;

export type Rate = FixedRate | DynamicRate;

const WeightConditionSchema = z.object({
  type: z.literal(ConditionalPricingCondition.WEIGHT),
  minGrams: z.number().min(0),
  maxGrams: z.number().min(0),
});
export type WeightCondition = z.infer<typeof WeightConditionSchema>;

const PriceConditionSchema = z.object({
  type: z.literal(ConditionalPricingCondition.PRICE),
  minPrice: z.number().min(0),
  maxPrice: z.number().min(0),
  currency: z.string(),
});
export type PriceCondition = z.infer<typeof PriceConditionSchema>;

const RateConditionSchema = z.union([
  WeightConditionSchema,
  PriceConditionSchema,
]);
export type RateCondition = z.infer<typeof RateConditionSchema>;

const BaseRateSchema = z.object({
  enabled: z.boolean().optional(),
  conditions: z.array(RateConditionSchema),
  currency: z.string(),
  merchantPaidCoverageTypes: z
    .object({
      return: z.boolean().optional(),
      packageProtection: z.boolean().optional(),
    })
    .optional(),
});

const BaseShippingRateTableSchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  description: z.string(),
  code: z.string(),
  type: z.nativeEnum(RateTableType),
  originLocations: z.array(ShippingRateLocationSchema),
  destinationLocations: z.array(ShippingRateLocationSchema),
  coverageTypes: z
    .object({
      return: z.boolean().optional(),
      packageProtection: z.boolean().optional(),
    })
    .optional(),
});

const FixedRateSchema = BaseRateSchema.extend({ price: z.string() });
export type FixedRate = z.infer<typeof FixedRateSchema>;

export const FixedShippingRateTableSchema = BaseShippingRateTableSchema.extend({
  type: z.literal(RateTableType.FIXED),
  rates: z.array(FixedRateSchema).min(1),
});
export type FixedShippingRateTable = z.infer<
  typeof FixedShippingRateTableSchema
>;

const CarrierAndServiceSchema = z.union([
  z.object({
    carrier: z.literal(Carriers.FEDEX),
    service: z.nativeEnum(FedexServices),
  }),
  z.object({
    carrier: z.literal(Carriers.UPS),
    service: z.nativeEnum(UpsServices),
  }),
  z.object({
    carrier: z.literal(Carriers.USPS),
    service: z.nativeEnum(UspsServices),
  }),
]);
export type CarrierAndService = z.infer<typeof CarrierAndServiceSchema>;

const DynamicRateSchema = BaseRateSchema.extend({
  markup: z
    .object({
      amount: z.string().optional(),
      percentage: z.string().optional(),
    })
    .optional(),
  carrierService: CarrierAndServiceSchema,
});
export type DynamicRate = z.infer<typeof DynamicRateSchema>;

export const DynamicShippingRateTableSchema =
  BaseShippingRateTableSchema.extend({
    type: z.literal(RateTableType.DYNAMIC),
    serviceLevelBucketedServiceSelection: z.nativeEnum(ServiceLevel).optional(),
    markup: z
      .object({
        amount: z
          .object({ currency: z.nativeEnum(Currency), value: z.string() })
          .optional(),
        percentage: z.string().optional(),
      })
      .optional(),
    rates: z.array(DynamicRateSchema),
    multipleDynamicRateSelection: z.nativeEnum(DynamicRateSelection).optional(),
  });
export type DynamicShippingRateTable = z.infer<
  typeof DynamicShippingRateTableSchema
>;

export const ShippingRateTableSchema = z.discriminatedUnion("type", [
  FixedShippingRateTableSchema,
  DynamicShippingRateTableSchema,
]);
export type ShippingRateTable = z.infer<typeof ShippingRateTableSchema>;

export const ShippingRateTableArraySchema = z.array(ShippingRateTableSchema);

export function isFixedRateTable(
  rateTable: ShippingRateTable,
): rateTable is FixedShippingRateTable {
  return rateTable.type === RateTableType.FIXED;
}

export function isDynamicRateTable(
  rateTable: ShippingRateTable,
): rateTable is DynamicShippingRateTable {
  return rateTable.type === RateTableType.DYNAMIC;
}

export function isFixedRate(
  rate: ShippingRateTable["rates"][number],
): rate is FixedRate {
  return "price" in rate;
}

export function isDynamicRate(
  rate: ShippingRateTable["rates"][number],
): rate is DynamicRate {
  return "markup" in rate;
}

export function isWeightCondition(
  condition: RateCondition,
): condition is WeightCondition {
  return condition.type === ConditionalPricingCondition.WEIGHT;
}

export function isPriceCondition(
  condition: RateCondition,
): condition is PriceCondition {
  return condition.type === ConditionalPricingCondition.PRICE;
}
